import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, CalendarDays, Check, CheckCircle2, Filter, MoreVertical, Pencil, Plus, RefreshCw, Search, ShieldCheck, Square, Trash2, X, XCircle } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import ApprovalStatusBadge from '../../components/common/ApprovalStatusBadge';
import UnsavedChangesDialog from '../../components/common/UnsavedChangesDialog';
import Toast from '../../components/Toast/Toast';
import { useTheme } from '../../context/ThemeContext';
import { defaultAvailabilityApprovalFilters, useAvailabilityApprovals } from '../../hooks/useAvailabilityApprovals';
import type { AvailabilityApprovalDetails, AvailabilityApprovalException, AvailabilityApprovalFiltersState, AvailabilityApprovalRow } from '../../types/admin';
import { getAvailabilityApprovalDetails, managerUpdateAvailability } from '../../services/availabilityApprovalService';
import { getAvailabilityTypes } from '../../constants/adminData';
import AvailabilityApprovalFilters, { getActiveAvailabilityFilterCount } from './AvailabilityApprovalFilters';
import AvailabilityApprovalsTable, { formatApprovalDate } from './AvailabilityApprovalsTable';
import { WeekRangeDatePicker } from '../../components/ui/WeekRangeDatePicker';
import { GetUSIDateTime } from '@/utils/useUtil';

const pageSizeOptions = [10, 20, 50, 100];

function DetailRow({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div>
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="mt-1 text-sm text-gray-900">{value || 'Not provided'}</p>
    </div>
  );
}

function AvailabilityChips({ values }: { values: string[] }) {
  if (!values.length) return <p className="text-sm text-gray-500">Not provided</p>;

  return (
    <div className="flex flex-wrap gap-2">
      {values.map((value) => (
        <span key={value} className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">
          {value}
        </span>
      ))}
    </div>
  );
}

const getInitials = (name: string) => name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase() || 'U';
const formatDateTime = (value?: string | null) => {
  if (!value) return { date: '—', time: '' };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: '—', time: '' };
  return { date: date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-'), time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) };
};

function formatCreatedDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

const formatExceptionDate = (value: string) => {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
};

// Keep the Manager Center exception dates consistent with My Availability:
// exceptions always run Monday through Sunday.
const parseDateInput = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};
const isMonday = (value: string) => parseDateInput(value)?.getDay() === 1;
const isSunday = (value: string) => parseDateInput(value)?.getDay() === 0;
const formatDateInput = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const getMonday = (value: string) => {
  const date = parseDateInput(value);
  if (!date) return '';
  const distanceFromMonday = date.getDay() === 0 ? -6 : 1 - date.getDay();
  date.setDate(date.getDate() + distanceFromMonday);
  return formatDateInput(date);
};
const getSunday = (value: string) => {
  const monday = parseDateInput(getMonday(value));
  if (!monday) return '';
  monday.setDate(monday.getDate() + 6);
  return formatDateInput(monday);
};
// Same rule as Complete Your Profile: exceptions cannot start before the current week.
// Existing exceptions keep their saved dates; only newly added ones are blocked from starting in the past.
const getCurrentWeekStart = () => getMonday(formatDateInput(new Date()));
// Outdated exceptions (ended before the current week) are hidden, matching Complete Your Profile.
const isOutdatedException = (item: AvailabilityApprovalException) => Boolean(item.endDate) && item.endDate < getCurrentWeekStart();
const getStartDateError = (value: string, isNew = false) => {
  if (!value || !isMonday(value)) return 'Please select a Monday.';
  return isNew && value < getCurrentWeekStart() ? 'Start date cannot be in the past.' : '';
};
const getEndDateError = (value: string, startDate: string) => {
  if (!value || !isSunday(value)) return 'Please select a Sunday.';
  const parsedEndDate = parseDateInput(value);
  const parsedStartDate = parseDateInput(startDate);
  return parsedEndDate && parsedStartDate && parsedEndDate < parsedStartDate ? 'End Date cannot be before Start Date.' : '';
};

// Keep the manager's regular-hours rule aligned with Complete Your Profile.
// In particular, an empty number input must not silently become zero.
const getDefaultHoursError = (value: string) => {
  if (value.trim() === '') return 'Default available hours per week is required.';

  const hours = Number(value);
  return Number.isFinite(hours) && hours >= 0 && hours <= 40
    ? ''
    : 'Default available hours per week must be between 0 and 40.';
};

const normalizeHours = (value: string) => {
  if (value === '') return '';
  const nextValue = Math.max(0, Math.min(40, Number(value)));
  return Number.isFinite(nextValue) ? String(nextValue) : '';
};

const normalizeRejectionReason = (value: string) => {
  const fourLines = value.split('\n').slice(0, 4).join('\n');
  const words = Array.from(fourLines.matchAll(/\S+/g));

  if (words.length <= 200) return fourLines;

  const lastAllowedWord = words[199];
  return fourLines.slice(0, (lastAllowedWord.index ?? 0) + lastAllowedWord[0].length);
};

const getCharacterLimitError = (value?: string | null) =>
  (value?.match(/\S/g) ?? []).length > 325 ? 'You have exceeded the character limit.' : '';

const normalizeAvailabilityDetails = (value: AvailabilityApprovalDetails) => ({
  // Exclude display-only and server-maintained fields so the dirty check only
  // reacts to values the manager can actually persist.
  availableHoursPerWeek: value.availableHoursPerWeek,
  availabilityTypeIds: [...value.availabilityTypeIds].sort((a, b) => a - b),
  notes: value.notes?.trim() || null,
  exceptions: value.exceptions
    .filter(item => !item.isDeleted)
    .map(item => ({ exceptionId: item.exceptionId ?? null, startDate: item.startDate, endDate: item.endDate, availableHours: item.availableHours, exceptionTypeId: item.exceptionTypeId ?? null, reason: item.reason?.trim() || null }))
    .sort((a, b) => `${a.startDate}|${a.endDate}|${a.exceptionId ?? ''}`.localeCompare(`${b.startDate}|${b.endDate}|${b.exceptionId ?? ''}`)),
});

function DetailsPanel({ row, onClose, onApprove, onReject, onSaved }: { row: AvailabilityApprovalRow; onClose: () => void; onApprove: () => void; onReject: () => void; onSaved: () => Promise<void> }) {
  const { theme } = useTheme();
  const availabilityId = row.availabilityIds?.[0];
  const [details, setDetails] = useState<AvailabilityApprovalDetails | null>(null);
  const [draft, setDraft] = useState<AvailabilityApprovalDetails | null>(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [pendingExit, setPendingExit] = useState<'close' | 'cancel' | null>(null);
  const [availabilityTypes, setAvailabilityTypes] = useState<Array<{ availabilityTypeId: number; availabilityTypeName: string }>>([]);
  const hoursInputRef = useRef<HTMLInputElement>(null);
  const [hoursInput, setHoursInput] = useState('');

  const load = async () => {
    if (!availabilityId) { setError('This request does not contain an availability record.'); setLoading(false); return; }
    setLoading(true); setError('');
    try { const value = await getAvailabilityApprovalDetails(availabilityId); setDetails(value); setDraft(structuredClone(value)); setHoursInput(String(value.availableHoursPerWeek)); }
    catch (err: any) { setError(err?.message || 'Failed to load availability details.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load().catch(() => {}); }, [availabilityId]);
  useEffect(() => {
    getAvailabilityTypes().then((response: any) => {
      const values = Array.isArray(response) ? response : Array.isArray(response?.data) ? response.data : [];
      setAvailabilityTypes(values);
    }).catch(() => setError('Failed to load availability types.'));
  }, []);

  const validation = useMemo(() => {
    if (!draft) return 'Details are not loaded.';
    const notesCharacterError = getCharacterLimitError(draft.notes);
    if (notesCharacterError) return notesCharacterError;
    const defaultHoursError = getDefaultHoursError(hoursInput);
    if (defaultHoursError) return defaultHoursError;
    const defaultHours = Number(hoursInput);
    const active = draft.exceptions.filter(x => !x.isDeleted && !isOutdatedException(x)).sort((a, b) => a.startDate.localeCompare(b.startDate));
    for (let i = 0; i < active.length; i++) {
      const item = active[i];
      const startDateError = getStartDateError(item.startDate, !item.exceptionId);
      if (startDateError) return startDateError;
      const endDateError = getEndDateError(item.endDate, item.startDate);
      if (endDateError) return endDateError;
      if (!Number.isFinite(item.availableHours) || item.availableHours < 0) return 'Exception hours cannot be negative.';
      if (item.availableHours > defaultHours) return 'Exception hours cannot exceed regular hours.';
      if (i > 0 && item.startDate <= active[i - 1].endDate) return 'Exception ranges cannot overlap.';
    }
    return '';
  }, [draft, hoursInput]);
  const changed = !!details && !!draft && (
    JSON.stringify(normalizeAvailabilityDetails(details)) !== JSON.stringify(normalizeAvailabilityDetails(draft)) ||
    (editing && Number(hoursInput) !== details.availableHoursPerWeek)
  );
  const closeDrawer = () => {
    if (changed) { setPendingExit('close'); return; }
    onClose();
  };
  const cancelEditing = () => {
    if (changed) { setPendingExit('cancel'); return; }
    if (!details) return;
    setDraft(structuredClone(details)); setHoursInput(String(details.availableHoursPerWeek)); setEditing(false); setError('');
  };
  const enterEditMode = () => {
    if (!details) return;
    setDraft(structuredClone(details)); setHoursInput(String(details.availableHoursPerWeek)); setError(''); setEditing(true);
    requestAnimationFrame(() => hoursInputRef.current?.focus());
  };
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') { event.preventDefault(); closeDrawer(); } };
    // Native beforeunload is intentionally limited to dirty edit mode; prompting
    // while merely reviewing an approval would make normal navigation disruptive.
    const handleBeforeUnload = (event: BeforeUnloadEvent) => { if (editing && changed) event.preventDefault(); };
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => { document.removeEventListener('keydown', handleKeyDown); window.removeEventListener('beforeunload', handleBeforeUnload); };
  }, [editing, changed]);
  const updateException = (index: number, patch: Partial<AvailabilityApprovalException>) => setDraft(current => current ? ({ ...current, exceptions: current.exceptions.map((item, i) => i === index ? { ...item, ...patch } : item) }) : current);
  const save = async () => {
    if (!draft || !availabilityId || validation) return false;
    setSaving(true); setError('');
    try {
      const value = await managerUpdateAvailability(availabilityId, { availabilityId, availableHoursPerWeek: Number(hoursInput), availabilityTypeIds: draft.availabilityTypeIds, notes: draft.notes?.trim() || null, version: draft.version, exceptions: draft.exceptions.map(item => ({ ...item, reason: item.reason?.trim() || null })) });
      setDetails(value); setDraft(structuredClone(value)); setHoursInput(String(value.availableHoursPerWeek)); setEditing(false); await onSaved(); await load();
      return true;
    } catch (err: any) { setError(err?.message || 'Unable to update availability. Please try again.'); }
    finally { setSaving(false); }
    return false;
  };
  const saveAndContinue = async () => {
    const exitAfterSave = pendingExit;
    if (!(await save())) return;
    setPendingExit(null);
    if (exitAfterSave === 'close') onClose();
  };
  const leaveWithoutSaving = () => {
    const exitAfterCancel = pendingExit;
    setPendingExit(null);
    if (exitAfterCancel === 'close') { onClose(); return; }
    if (details) { setDraft(structuredClone(details)); setHoursInput(String(details.availableHoursPerWeek)); }
    setEditing(false);
    setError('');
  };
  const addException = () => {
    const current = editing ? draft : details;
    if (!current) return;
    setDraft({ ...structuredClone(current), exceptions: [...current.exceptions, { exceptionId: null, startDate: '', endDate: '', availableHours: current.availableHoursPerWeek, reason: '' }] });
    setEditing(true);
  };

  const submitted = GetUSIDateTime(details?.submittedDate);

  const activeExceptions = draft?.exceptions.map((item, index) => ({ item, index })).filter(({ item }) => !item.isDeleted && !isOutdatedException(item)) ?? [];
  const isPending = String(details?.approvalStatus || '').trim().toLowerCase() === 'pending';
  const isApproved = String(details?.approvalStatus || '').trim().toLowerCase() === 'approved';
  const isRejected = String(details?.approvalStatus || '').trim().toLowerCase() === 'rejected';


  return (
    <div className="fixed inset-0 z-[80] flex justify-end bg-black/40" role="dialog" aria-modal="true" aria-label="Availability details" onMouseDown={event => { if (event.target === event.currentTarget) closeDrawer(); }}>
      <div className="flex h-full w-full flex-col bg-[#f8fafc] shadow-2xl sm:w-[min(68vw,calc(100vw-2rem))] sm:max-w-[1100px]">
       <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="sticky -top-4 z-20 -mx-4 -mt-4 mb-6 flex items-center justify-between gap-4 border-b border-gray-200 bg-white px-4 py-4 sm:-top-6 sm:-mx-6 sm:-mt-6 sm:px-6">
          <div className="flex min-w-0 items-center gap-3"><div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-brand-primary text-sm font-bold text-white">{details?.employee.profileImageUrl ? <img src={details.employee.profileImageUrl} alt="" className="h-full w-full object-cover" /> : getInitials(details?.employee.name || row.userName)}</div><div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><h2 className="truncate text-lg font-bold text-gray-950">{details?.employee.name || row.userName}</h2>{details && <ApprovalStatusBadge status={details.approvalStatus} />}</div><p className="truncate text-sm text-gray-500">{details?.employee.email || 'Email not provided'}</p></div></div>
          <div className="ml-auto hidden text-right sm:block"><p className="text-xs text-gray-500">Submitted on</p><p className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-gray-800"><CalendarDays className="h-4 w-4 text-gray-400" />{submitted.date}</p></div>
          <button type="button" onClick={closeDrawer} className="rounded-full p-2 text-gray-500 hover:bg-gray-100" aria-label="Close availability details">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading && <p className="py-12 text-center text-sm text-gray-500">Loading availability details...</p>}
        {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
        {!loading && details && draft && <div className="space-y-5">
          <div className="grid grid-cols-1 divide-y rounded-xl border border-gray-200 bg-white shadow-sm sm:grid-cols-2 sm:divide-x sm:divide-y-0 lg:grid-cols-3">
            <div className="p-4"><DetailRow label="Status" value={details.approvalStatus} /></div>
            <div className="p-4"><p className="text-xs font-medium text-gray-500">Submitted On</p><p className="mt-1 text-sm font-semibold text-gray-900">{submitted.date}</p><p className="text-xs text-gray-500">{submitted.time}</p></div>
            <div className="p-4"><p className="text-xs font-medium text-gray-500">Submitted By</p><p className="mt-1 text-sm font-semibold text-gray-900">{details.employee.name}</p><p className="text-xs text-gray-500">Employee</p></div>
            {/* <div className="p-4"><DetailRow label="Notes" value={details.notes} /></div> */}
          </div>
          <div>
            <div className="mb-3 flex items-center justify-between gap-3"><h3 className="flex items-center gap-2 text-base font-bold text-gray-950"><CalendarDays className="h-5 w-5 text-brand-primary" />Availability</h3>{!editing && isPending && details.canEdit && <button type="button" onClick={enterEditMode} className="inline-flex items-center rounded-lg border border-brand-primary bg-blue-50 px-3 py-2 text-sm font-semibold text-brand-primary"><Pencil className="mr-2 h-4 w-4" />Edit Availability</button>}</div>
            <div className="grid grid-cols-1 gap-5 rounded-xl border border-gray-200 bg-white p-5 shadow-sm md:grid-cols-2 lg:grid-cols-3">
              <div><p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Hours per week</p>{editing ? <><input ref={hoursInputRef} type="number" min="0" max="40" value={hoursInput} onChange={e => setHoursInput(normalizeHours(e.target.value))} aria-invalid={Boolean(getDefaultHoursError(hoursInput))} className={`mt-2 h-11 w-full rounded-lg border px-3 ${getDefaultHoursError(hoursInput) ? 'border-red-500' : ''}`} style={{ backgroundColor: theme.surfaceHover, borderColor: getDefaultHoursError(hoursInput) ? undefined : theme.border, color: theme.text }} />{getDefaultHoursError(hoursInput) && <p className="mt-1 text-xs text-red-600">{getDefaultHoursError(hoursInput)}</p>}</> : <p className="mt-2 text-2xl font-bold text-gray-950">{draft.availableHoursPerWeek}</p>}</div>
              <div className="text-xs font-medium text-gray-600"><span className="font-semibold uppercase tracking-wide text-gray-500">Available For</span><div className="mt-2 grid gap-2">{availabilityTypes.map(type => { const selected = draft.availabilityTypeIds.includes(type.availabilityTypeId); return editing ? <label key={type.availabilityTypeId} className="flex cursor-pointer items-center gap-2 text-sm text-gray-800"><input type="checkbox" checked={selected} onChange={() => setDraft({ ...draft, availabilityTypeIds: selected ? draft.availabilityTypeIds.filter(id => id !== type.availabilityTypeId) : [...draft.availabilityTypeIds, type.availabilityTypeId] })} className="h-4 w-4 accent-brand-primary" />{type.availabilityTypeName}</label> : <div key={type.availabilityTypeId} className={`flex items-center gap-2 text-sm ${selected ? 'font-medium text-gray-900' : 'text-gray-400'}`}>{selected ? <CheckCircle2 className="h-4 w-4 text-brand-primary" /> : <Square className="h-4 w-4" />}{type.availabilityTypeName}</div>; })}</div></div>
              <div className="md:col-span-2 lg:col-span-1"><p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Notes</p>{editing ? <><textarea value={draft.notes || ''} onChange={e => setDraft({ ...draft, notes: normalizeRejectionReason(e.target.value) })} rows={4} aria-invalid={Boolean(getCharacterLimitError(draft.notes))} className={`mt-2 w-full rounded-lg border p-3 ${getCharacterLimitError(draft.notes) ? 'border-red-500' : 'border-gray-300'}`} style={{ backgroundColor: theme.surfaceHover, borderColor: getCharacterLimitError(draft.notes) ? undefined : theme.border, color: theme.text }} />{getCharacterLimitError(draft.notes) && <p className="mt-1 text-xs text-red-600">{getCharacterLimitError(draft.notes)}</p>}</> : <p className="mt-2 text-sm text-gray-800">{draft.notes?.trim() || 'Not provided'}</p>}</div>
            </div>
          </div>
          <div>
            <div className="mb-3 flex items-center justify-between gap-3"><h3 className="flex items-center gap-2 text-base font-bold text-gray-950"><CalendarDays className="h-5 w-5 text-brand-primary" />Exceptions</h3>{isPending && details.canEdit && <button type="button" onClick={addException} className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"><Plus className="mr-2 h-4 w-4" />Add Exception</button>}</div>
            {!activeExceptions.length && <p className="rounded-xl border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500">No availability exceptions added.</p>}
            {editing ? <div className="space-y-3">{activeExceptions.map(({ item, index }) => <div key={item.exceptionId ?? `new-${index}`} className="relative grid grid-cols-1 gap-3 rounded-xl border border-gray-200 bg-white p-4 pt-12 shadow-sm sm:grid-cols-2">
              <label className="text-xs text-gray-600">Start date<WeekRangeDatePicker disabled={!editing} value={item.startDate} min={getCurrentWeekStart()} rangeStart={item.startDate} rangeEnd={item.endDate || getSunday(item.startDate)} ariaInvalid={Boolean(getStartDateError(item.startDate, !item.exceptionId))} onChange={value => updateException(index, { startDate: getMonday(value), endDate: getSunday(value) })} className={`mt-2 h-14 w-full rounded-xl border-2 bg-blue-50 px-4 text-lg font-medium text-slate-800 outline-none focus:border-blue-400 disabled:bg-gray-50 ${getStartDateError(item.startDate, !item.exceptionId) ? 'border-red-500' : 'border-blue-200'}`} />{getStartDateError(item.startDate, !item.exceptionId) && <p className="mt-1 text-xs normal-case tracking-normal text-red-600">{getStartDateError(item.startDate, !item.exceptionId)}</p>}</label>
              <label className="text-xs text-gray-600">End date<WeekRangeDatePicker disabled={!editing} value={item.endDate} min={item.startDate || getCurrentWeekStart()} rangeStart={item.startDate} rangeEnd={item.endDate || getSunday(item.startDate)} ariaInvalid={Boolean(getEndDateError(item.endDate, item.startDate))} onChange={value => updateException(index, { endDate: getSunday(value) })} className={`mt-2 h-14 w-full rounded-xl border-2 bg-blue-50 px-4 text-lg font-medium text-slate-800 outline-none focus:border-blue-400 disabled:bg-gray-50 ${getEndDateError(item.endDate, item.startDate) ? 'border-red-500' : 'border-blue-200'}`} />{getEndDateError(item.endDate, item.startDate) && <p className="mt-1 text-xs normal-case tracking-normal text-red-600">{getEndDateError(item.endDate, item.startDate)}</p>}</label>
              <label className="text-xs text-gray-600">Available hours<input type="number" min="0" disabled={!editing} value={item.availableHours} onKeyDown={e => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }} onChange={e => updateException(index, { availableHours: Math.max(0, Number(e.target.value) || 0) })} className="mt-1 h-9 w-full rounded border px-2" style={{ backgroundColor: editing ? theme.surfaceHover : theme.surface, borderColor: theme.border, color: editing ? theme.text : theme.textSecondary }} /></label>
              <label className="text-xs text-gray-600">Reason (optional)<input disabled={!editing} value={item.reason || ''} placeholder="No reason provided" onChange={e => updateException(index, { reason: e.target.value })} className="mt-1 h-9 w-full rounded border px-2" style={{ backgroundColor: editing ? theme.surfaceHover : theme.surface, borderColor: theme.border, color: editing ? theme.text : theme.textSecondary }} />{!editing && !item.reason?.trim() && <span className="mt-1 block text-xs text-gray-500">—</span>}</label>
              {editing && <button type="button" onClick={() => { if (!item.exceptionId || window.confirm('Delete this exception?')) updateException(index, { isDeleted: true }); }} className="absolute right-3 top-3 rounded-lg p-2 text-red-600 transition hover:bg-red-50" aria-label="Delete exception" title="Delete exception"><Trash2 className="h-4 w-4" /></button>}
            </div>)}</div> : activeExceptions.length > 0 && <>
              <div className="hidden overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm md:block"><table className="w-full text-left text-sm"><thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500"><tr><th className="px-4 py-3">Date range</th><th className="px-4 py-3">Hours</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Reason</th><th className="w-14 px-4 py-3"><span className="sr-only">Actions</span></th></tr></thead><tbody className="divide-y divide-gray-100">{activeExceptions.map(({ item }) => <tr key={item.exceptionId ?? `${item.startDate}-${item.endDate}`}><td className="px-4 py-3 font-medium text-gray-900">{formatExceptionDate(item.startDate)} – {formatExceptionDate(item.endDate)}</td><td className="px-4 py-3 text-gray-700">{item.availableHours}</td><td className="px-4 py-3 text-gray-700">Default availability types</td><td className="max-w-[240px] truncate px-4 py-3 text-gray-600">{item.reason?.trim() || '—'}</td><td className="px-4 py-3 text-right">{isPending && details.canEdit && <button type="button" onClick={enterEditMode} className="rounded-full p-2 text-gray-500 hover:bg-gray-100" aria-label="Edit exception"><MoreVertical className="h-4 w-4" /></button>}</td></tr>)}</tbody></table></div>
              <div className="space-y-3 md:hidden">{activeExceptions.map(({ item }) => <div key={item.exceptionId ?? `${item.startDate}-${item.endDate}`} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"><div className="flex items-start justify-between gap-3"><p className="font-semibold text-gray-900">{formatExceptionDate(item.startDate)} – {formatExceptionDate(item.endDate)}</p>{isPending && details.canEdit && <button type="button" onClick={enterEditMode} className="rounded-full p-1.5 text-gray-500" aria-label="Edit exception"><MoreVertical className="h-4 w-4" /></button>}</div><div className="mt-3 grid grid-cols-2 gap-3 text-sm"><DetailRow label="Hours" value={String(item.availableHours)} /><DetailRow label="Type" value="Default availability types" /><div className="col-span-2"><DetailRow label="Reason" value={item.reason} /></div></div></div>)}</div>
            </>}
            {editing && validation && <p className="mt-2 text-xs text-red-600">{validation}</p>}
          </div>
          {!isPending && 
          <div>
            <h3 className="mb-3 flex items-center gap-2 text-base font-bold text-gray-950"><ShieldCheck className="h-5 w-5 text-brand-primary" />Approval Information</h3>
            <div className="grid grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm sm:grid-cols-2 lg:grid-cols-5">
           
            {isApproved &&
            <>
              <DetailRow label="Approved By" value={row?.approvedBy} />
              <DetailRow label="Approved On" value={formatApprovalDate(row.approvedDate)} />
              </>
            }
            {isRejected &&
            <>
              <DetailRow label="Rejected By" value={row?.rejectedBy} />
              <DetailRow label="Rejected On" value={formatApprovalDate(row.rejectedDate)} />
              <DetailRow label="Rejection Reason" value={row.rejectionReason} />
              </>
            }
            </div>
          </div>
          }
        </div>}
       </div>
       {!loading && details && draft && <div className="z-10 flex flex-col gap-3 border-t bg-white p-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
         {!editing ? <><button type="button" onClick={closeDrawer} className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 sm:w-auto">Close</button>{String(details.approvalStatus || '').trim().toLowerCase() === 'pending' && <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:justify-end">{details.canReject && <button type="button" onClick={onReject} className="inline-flex items-center justify-center rounded-lg border border-red-300 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"><XCircle className="mr-2 h-4 w-4" />Reject</button>}{details.canApprove && <button type="button" onClick={onApprove} className="inline-flex items-center justify-center rounded-lg bg-brand-primary px-4 py-2 text-sm font-semibold text-white"><Check className="mr-2 h-4 w-4" />Approve</button>}</div>}</> : <div className="ml-auto flex w-full flex-col gap-2 sm:w-auto sm:flex-row"><button type="button" onClick={cancelEditing} disabled={saving} className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 sm:w-auto">Cancel</button><button type="button" onClick={save} disabled={saving || !changed || !!validation} className="w-full rounded-lg bg-brand-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-40 sm:w-auto">{saving ? 'Saving...' : 'Save Changes'}</button></div>}
       </div>}
      </div>
      <UnsavedChangesDialog isOpen={Boolean(pendingExit)} onConfirm={leaveWithoutSaving} onSave={saveAndContinue} />
    </div>
  );
}

function ApproveModal({
  loading,
  onCancel,
  onConfirm,
}: {
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 px-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
        <h2 className="mb-3 text-lg font-semibold text-gray-900">Approve Availability Request</h2>
        <p className="mb-6 text-sm text-gray-700">Are you sure you want to approve this request?</p>
        <div className="grid grid-cols-2 gap-3">
          <button type="button" onClick={onCancel} disabled={loading} className="h-11 rounded-lg bg-gray-100 text-sm font-semibold text-gray-800">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={loading} className="h-11 rounded-lg bg-brand-primary text-sm font-semibold text-white disabled:bg-gray-300">
            {loading ? 'Approving...' : 'Approve'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RejectModal({
  loading,
  reason,
  error,
  onReasonChange,
  onCancel,
  onConfirm,
}: {
  loading: boolean;
  reason: string;
  error: string;
  onReasonChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const trimmedReason = reason.trim();
  const characterLimitError = getCharacterLimitError(reason);
  const reasonValidationError = !trimmedReason
    ? ''
    : characterLimitError
      ? characterLimitError
      : trimmedReason.length < 5
      ? 'Rejection reason must be at least 5 characters.'
      : '';
  const canReject = trimmedReason.length >= 5 && !characterLimitError;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 px-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Reject Availability Request</h2>
        <label className="mb-2 block text-sm font-medium text-gray-700">Rejection Reason</label>
        <textarea
          value={reason}
          onChange={(event) => onReasonChange(normalizeRejectionReason(event.target.value))}
          rows={4}
          aria-invalid={Boolean(error || reasonValidationError)}
          className={`w-full resize-none rounded-lg border px-3 py-2 text-sm outline-none focus:border-brand-primary ${error || reasonValidationError ? 'border-red-500' : 'border-gray-300'}`}
          placeholder="Manager comments"
        />
        {(error || reasonValidationError) && <p className="mt-2 text-xs text-red-600">{error || reasonValidationError}</p>}
        <div className="mt-5 grid grid-cols-2 gap-3">
          <button type="button" onClick={onCancel} disabled={loading} className="h-11 rounded-lg bg-gray-100 text-sm font-semibold text-gray-800">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={loading || !canReject} className="h-11 rounded-lg bg-red-600 text-sm font-semibold text-white disabled:bg-gray-300">
            {loading ? 'Rejecting...' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  );
}

function getPageNumbers(pageNumber: number, totalPages: number) {
  const start = Math.max(1, pageNumber - 2);
  const end = Math.min(totalPages, start + 4);
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

export default function AvailabilityApprovals() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const {
    rows,
    recordsTotal,
    recordsFiltered,
    pendingCount,
    pageNumber,
    pageSize,
    search,
    sortColumn,
    sortDirection,
    filters,
    loading,
    isFetching,
    actionLoadingKey,
    error,
    setSearch,
    setPageNumber,
    changePageSize,
    changeSort,
    applyFilters,
    resetFilters,
    refresh,
    approve,
    reject,
  } = useAvailabilityApprovals();

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState<AvailabilityApprovalFiltersState>(filters);
  const [selectedRow, setSelectedRow] = useState<AvailabilityApprovalRow | null>(null);
  const [approveRow, setApproveRow] = useState<AvailabilityApprovalRow | null>(null);
  const [rejectRow, setRejectRow] = useState<AvailabilityApprovalRow | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionError, setRejectionError] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);
  const drawerTriggerRef = useRef<HTMLElement | null>(null);
  const notificationTargetHandledRef = useRef(false);

  useEffect(() => {
    const openAvailabilityId = Number((location.state as { openAvailabilityId?: number } | null)?.openAvailabilityId);
    if (!openAvailabilityId || loading || notificationTargetHandledRef.current) return;

    notificationTargetHandledRef.current = true;
    const matchingRow = rows.find((row) => row.availabilityIds?.includes(openAvailabilityId));
    if (matchingRow) {
      setSelectedRow(matchingRow);
    } else {
      setToast({ message: 'This availability request is no longer pending or is unavailable.', type: 'warning' });
    }

    navigate(location.pathname, { replace: true, state: null });
  }, [loading, location.pathname, location.state, navigate, rows]);

  const openDetails = (row: AvailabilityApprovalRow) => {
    drawerTriggerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setSelectedRow(row);
  };
  const closeDetails = () => {
    setSelectedRow(null);
    requestAnimationFrame(() => drawerTriggerRef.current?.focus());
  };

  const activeFilterCount = getActiveAvailabilityFilterCount(filters);
  const totalPages = Math.max(1, Math.ceil(recordsFiltered / pageSize));
  const pageStart = recordsFiltered === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
  const pageEnd = Math.min(pageNumber * pageSize, recordsFiltered);
  const pageNumbers = useMemo(() => getPageNumbers(pageNumber, totalPages), [pageNumber, totalPages]);

  const handleOpenFilters = () => {
    setDraftFilters(filters);
    setFiltersOpen(true);
  };

  const handleApplyFilters = () => {
    applyFilters(draftFilters);
    setFiltersOpen(false);
  };

  const handleResetFilters = () => {
    setDraftFilters(defaultAvailabilityApprovalFilters);
    resetFilters();
    setFiltersOpen(false);
  };

  const handleApprove = async () => {
    if (!approveRow) return;

    try {
      await approve(approveRow);
      setToast({ message: 'Availability approved successfully.', type: 'success' });
      setApproveRow(null);
      setSelectedRow(null);
    } catch (err: any) {
      setToast({ message: err?.message || 'Failed to approve availability.', type: 'error' });
    }
  };

  const handleReject = async () => {
    if (!rejectRow) return;

    const reason = rejectionReason.trim();
    if (!reason) {
      setRejectionError('Rejection reason is required.');
      return;
    }

    if (reason.length < 5) {
      setRejectionError('Rejection reason must be at least 5 characters.');
      return;
    }

    try {
      await reject(rejectRow, reason);
      setToast({ message: 'Availability rejected successfully.', type: 'success' });
      setRejectRow(null);
      setSelectedRow(null);
      setRejectionReason('');
      setRejectionError('');
    } catch (err: any) {
      setToast({ message: err?.message || 'Failed to reject availability.', type: 'error' });
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f7fa]">
      <div className="mx-auto w-full max-w-7xl px-4 py-4">
        <div className="mb-5 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex h-11 w-11 items-center justify-center rounded-lg bg-gray-50"
                style={{ color: theme.primary }}
                aria-label="Back"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-xl font-bold text-gray-900">Availability Approvals</h1>
                  <span className="inline-flex min-w-7 items-center justify-center rounded-full bg-red-600 px-2 py-1 text-xs font-bold text-white">
                    {pendingCount}
                  </span>
                </div>
                <p className="mt-1 text-sm text-gray-500">Review pending employee availability requests.</p>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search employee..."
                  className="h-11 w-full rounded-lg border border-gray-300 pl-9 pr-3 text-sm outline-none focus:border-brand-primary sm:w-64"
                  style={{ backgroundColor: theme.surface, borderColor: theme.border, color: theme.text }}
                />
              </div>
              <button
                type="button"
                onClick={() => refresh().catch(() => {})}
                disabled={isFetching}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-gray-200 px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                type="button"
                onClick={handleOpenFilters}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-brand-primary px-4 text-sm font-semibold text-white"
              >
                <Filter className="h-4 w-4" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="rounded-full bg-white px-2 py-0.5 text-xs font-bold text-brand-primary">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-700">{error}</p>
            <button type="button" onClick={() => refresh().catch(() => {})} className="mt-3 text-sm font-semibold text-red-700">
              Retry
            </button>
          </div>
        )}

        {rows.length === 0 && !loading ? (!error && (
          <div className="rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm">
            <p className="text-sm text-gray-500">No pending availability requests found.</p>
          </div>
        )) : (
          <div className={`transition ${isFetching && !loading ? 'opacity-70' : 'opacity-100'}`}>
            <AvailabilityApprovalsTable
              rows={rows}
              loading={loading}
              actionLoadingKey={actionLoadingKey}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={changeSort}
              onView={openDetails}
              onApprove={setApproveRow}
              onReject={(row) => {
                setRejectRow(row);
                setRejectionReason('');
                setRejectionError('');
              }}
            />
          </div>
        )}

        <div className="mt-4 flex flex-col gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <p className="mb-0 text-sm text-gray-600">
              Showing {pageStart}-{pageEnd} of {recordsFiltered} requests
              {recordsTotal !== recordsFiltered ? ` (${recordsTotal} total)` : ''}
            </p>
            <select
              value={pageSize}
              onChange={(event) => changePageSize(Number(event.target.value))}
            className="h-10 rounded-lg border border-gray-200 pl-3 pr-7 text-sm outline-none"
            style={{ backgroundColor: theme.surface, borderColor: theme.border, color: theme.text }}
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={pageNumber <= 1 || isFetching}
              onClick={() => setPageNumber(Math.max(pageNumber - 1, 1))}
              className="h-10 rounded-lg border border-gray-200 px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
            {pageNumbers.map((page) => (
              <button
                key={page}
                type="button"
                disabled={isFetching}
                onClick={() => setPageNumber(page)}
                className={`h-10 min-w-10 rounded-lg px-3 text-sm font-semibold ${
                  page === pageNumber
                    ? 'bg-brand-primary text-white'
                    : 'border border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              disabled={pageNumber >= totalPages || isFetching}
              onClick={() => setPageNumber(Math.min(pageNumber + 1, totalPages))}
              className="h-10 rounded-lg border border-gray-200 px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <AvailabilityApprovalFilters
        open={filtersOpen}
        filters={draftFilters}
        onChange={setDraftFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        onClose={() => setFiltersOpen(false)}
      />

      {selectedRow && <DetailsPanel row={selectedRow} onClose={closeDetails} onApprove={() => setApproveRow(selectedRow)} onReject={() => setRejectRow(selectedRow)} onSaved={async () => { await refresh(); setToast({ message: 'Availability updated successfully. The employee has been notified.', type: 'success' }); }} />}

      {approveRow && (
        <ApproveModal
          loading={actionLoadingKey === `${approveRow.userId}-${approveRow.submittedDate}`}
          onCancel={() => setApproveRow(null)}
          onConfirm={handleApprove}
        />
      )}

      {rejectRow && (
        <RejectModal
          loading={actionLoadingKey === `${rejectRow.userId}-${rejectRow.submittedDate}`}
          reason={rejectionReason}
          error={rejectionError}
          onReasonChange={(value) => {
            setRejectionReason(value);
            if (value.trim().length >= 5) setRejectionError('');
          }}
          onCancel={() => setRejectRow(null)}
          onConfirm={handleReject}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
